import { faUserEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
 DialogContent, Grid, TextField, ToggleButton, ToggleButtonGroup
} from '@mui/material';

import { FormElement } from 'components';
import { useStores } from 'hooks';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useState } from 'react';
import {
 useForm, Controller
} from 'react-hook-form';

import styled from 'styled-components';
import { ADMIN_ROLES } from 'utils/constants';
import { FormHelper, translate, UserHelper } from 'utils/helpers';
import { ModalHeader } from './ModalHeader';
import { CustomActionButtons } from './_CustomActionButtons';

const UpdateUserForm = styled.form`
  overflow: auto;
  
  .MuiToggleButton-root,
  .MuiToggleButton-root:hover {
    background-color: var(--white);
  }

  .MuiToggleButton-root.Mui-selected:first-child,
  .MuiToggleButton-root.Mui-selected:first-child:hover {
    background-color: var(--green);
  }

  .MuiToggleButton-root.Mui-selected:last-child,
  .MuiToggleButton-root.Mui-selected:last-child:hover {
    background-color: var(--red);
  }

  @media(min-width: 768px) {
    width: 680px;
  }
`;

interface UpdateUserModalProps {
  closeOnSubmit?: boolean;
  defaultValues: {
    accountActivated: boolean;
    accountId: string | null;
    canEditEmail: boolean;
    login?: string;
    mainCompany: any;
    modelId: string;
    person: {
      address: {
        address1?: string;
        address2?: string;
        city?: string;
        postalCode?: string;
      };
      attestationNumber?: string;
      contact: {
        contactEmails?: string;
        firstContactEmail?: string;
        phoneNumber?: string;
        technicalEmail?: string;
      };
      firstName?: string;
      lastName?: string;
      occupation?: string;
    };
    personId: string;
  };
  onClose: () => void;
  onConfirm: (data: any) => void;
}

export const UpdateUserModal = observer(({
                                           onClose, onConfirm, closeOnSubmit = true, defaultValues
                                         }: UpdateUserModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { userStore } = useStores();
  const { scope } = userStore;

  // Initialize form with default values
  // Define form field types
  interface FormValues {
    accountActivated: boolean;
    accountId: string | null;
    address1: string;
    address2: string;
    attestationNumber: string;
    birthDate?: string;
    city: string;
    contactEmails: string;
    country: any;
    // Changed from string to any to accommodate CountryView type
    email: string;
    firstContactEmail: string;
    firstName: string;
    lastName: string;
    login: string;
    occupation: string;
    phoneNumber: string;
    postalCode: string;
  }

  const formDefaultValues: FormValues = {
    accountId: defaultValues.accountId !== null ? defaultValues.accountId : null,
    accountActivated: defaultValues.accountActivated !== null ? defaultValues.accountActivated : true,
    login: defaultValues.login ?? '',
    firstName: defaultValues.person.firstName ?? '',
    lastName: defaultValues.person.lastName ?? '',
    attestationNumber: defaultValues.person.attestationNumber ?? '',
    firstContactEmail: defaultValues.person.contact.firstContactEmail ?? '',
    phoneNumber: defaultValues.person.contact.phoneNumber ?? '',
    occupation: defaultValues.person.occupation ?? '',
    address1: defaultValues.person.address.address1 ?? '',
    address2: defaultValues.person.address.address2 ?? '',
    postalCode: defaultValues.person.address.postalCode ?? '',
    city: defaultValues.person.address.city ?? '',
    country: UserHelper.getDefaultCountry(),
    email: defaultValues.person.contact.technicalEmail ?? '',
    contactEmails: defaultValues.person.contact.contactEmails ?? ''
  };

  const {
 control, handleSubmit, formState: { errors }, watch
} = useForm<FormValues>({
    defaultValues: formDefaultValues
  });

  // Watch values needed for conditional rendering or validation
  const accountActivated = watch('accountActivated');
  const address1 = watch('address1');
  const postalCode = watch('postalCode');
  const city = watch('city');

  const canAdministrate = () => !(!UserHelper.hasAccessRight(ADMIN_ROLES) || defaultValues.accountId === scope.accountId);

  const onSubmit = useCallback((data: FormValues) => {
    setIsLoading(true);

    onConfirm({
      accountActivated: data.accountActivated,
      accountId: defaultValues.accountId,
      personId: defaultValues.personId,
      modelId: defaultValues.modelId,
      login: data.login,
      person: {
        ...defaultValues.person,
        address: {
          address1: data.address1,
          address2: data.address2,
          postalCode: data.postalCode,
          city: data.city
        },
        birthDate: data.birthDate,
        firstName: data.firstName,
        lastName: data.lastName,
        attestationNumber: data.attestationNumber,
        contact: {
          technicalEmail: data.email,
          contactEmails: data.contactEmails,
          phoneNumber: data.phoneNumber
        },
        mainCompany: defaultValues.mainCompany,
        occupation: data.occupation
      }
    });

    if (closeOnSubmit) {
      onClose();
    }
  }, [closeOnSubmit, onClose, onConfirm, defaultValues]);

  return (
    <UpdateUserForm autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader onClose={onClose}>
        <Grid alignItems="center" container justifyContent="space-between">
          <div>
            <FontAwesomeIcon icon={faUserEdit} />
            {`${translate('modalUserUpdate.editUser')} : ${watch('login')}`}
          </div>
        </Grid>
      </ModalHeader>

      <DialogContent style={{ paddingTop: '10px' }}>
        <Grid container direction="column">
          <FormElement label={translate('pageUserDetail.myInfos')}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="lastName"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.lastName}
                              label={translate('common.lastName')}
                              required
                          />
                      )}
                      rules={{ required: true }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="firstName"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.firstName}
                              label={translate('common.firstName')}
                              required
                          />
                      )}
                      rules={{ required: true }}
                  />
              </Grid>
            </Grid>
            <Grid container>
              <Controller
                    control={control}
                    name="occupation"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            autoComplete="new-password"
                            error={!!errors.occupation}
                            inputProps={{ maxLength: 200 }}
                            label={translate('common.occupation')}
                        />
                    )}
                />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Controller
                    control={control}
                    name="attestationNumber"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            autoComplete="new-password"
                            error={!!errors.attestationNumber}
                            label={translate('common.capacityAttestationNumber')}
                        />
                    )}
                />
            </Grid>
          </FormElement>
          <FormElement label={translate('common.contact')}>
            <Grid item sm={12}>
              <Controller
                    control={control}
                    name="contactEmails"
                    render={({ field }) => (
                      <TextField
                            {...field}
                            autoComplete="new-password"
                            disabled={!defaultValues.canEditEmail}
                            error={!!errors.contactEmails}
                            helperText={errors.contactEmails?.message as React.ReactNode}
                            label={translate('common.contactEmails.modal')}
                        />
                    )}
                    rules={{
                      validate: value => FormHelper.validateEmails(value)
                    }}
                />
            </Grid>
            <Grid container spacing={2}>
              {accountActivated && (
                <Grid item sm={6} xs={12}>
                  <Controller
                          control={control}
                          name="email"
                          render={({ field }) => (
                            <TextField
                                  {...field}
                                  autoComplete="new-password"
                                  error={!!errors.email}
                                  helperText={errors.email?.message as React.ReactNode}
                                  label={translate('common.userModal.technicalEmail')}
                                  required
                                  type="email"
                              />
                          )}
                          rules={{
                            required: true,
                            validate: value => FormHelper.validateEmail(value)
                          }}
                      />
                </Grid>
                )}
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="phoneNumber"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.phoneNumber}
                              label={translate('common.phone')}
                          />
                      )}
                  />
              </Grid>
            </Grid>
          </FormElement>
          <FormElement label={translate('common.address')}>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="address1"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.address1}
                              label={translate('common.address')}
                              required={FormHelper.isFieldRequired({
                                fieldsToCheck: [postalCode, city]
                              })}
                          />
                      )}
                      rules={{
                        required: FormHelper.isFieldRequired({
                          fieldsToCheck: [postalCode, city]
                        })
                      }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="address2"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.address2}
                              label={translate('common.address2')}
                          />
                      )}
                  />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="postalCode"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.postalCode}
                              label={translate('common.postalCode')}
                              required={FormHelper.isFieldRequired({
                                fieldsToCheck: [address1, city]
                              })}
                              style={{ maxWidth: '100px' }}
                          />
                      )}
                      rules={{
                        required: FormHelper.isFieldRequired({
                          fieldsToCheck: [address1, city]
                        })
                      }}
                  />
              </Grid>
              <Grid item sm={6} xs={12}>
                <Controller
                      control={control}
                      name="city"
                      render={({ field }) => (
                        <TextField
                              {...field}
                              autoComplete="new-password"
                              error={!!errors.city}
                              label={translate('common.city')}
                              required={FormHelper.isFieldRequired({
                                fieldsToCheck: [address1, postalCode]
                              })}
                          />
                      )}
                      rules={{
                        required: FormHelper.isFieldRequired({
                          fieldsToCheck: [address1, postalCode]
                        })
                      }}
                  />
              </Grid>
              {/* This input is only used for forcing disabling autocomplete in chrome browser */}
              <input name="fakeInputForForcingDisablingAutocompleteChrome4" style={{ display: 'none' }} type="text" />
            </Grid>
          </FormElement>

          {canAdministrate() && (
            <>
              <FormElement label={translate('common.userAccount')}>
                <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                    {canAdministrate() && (
                    <div>
                      <Controller
                                  control={control}
                                  name="accountActivated"
                                  render={({ field: { onChange, value } }) => (
                                    <ToggleButtonGroup
                                          aria-label={translate('modalUserUpdate.userStatus')}
                                          exclusive
                                          size="small"
                                          value={value}
                                          onChange={(event, newValue) => {
                                            if (newValue === null) return;
                                            onChange(newValue);
                                          }}
                                      >
                                      <ToggleButton aria-label={translate('common.activated')} value={true}>{translate('common.activated')}</ToggleButton>
                                      <ToggleButton aria-label={translate('common.deactivated')} value={false}>{translate('common.deactivated')}</ToggleButton>
                                    </ToggleButtonGroup>
                                  )}
                              />
                    </div>
                        )}
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    {accountActivated && (
                    <Controller
                                control={control}
                                name="login"
                                render={({ field }) => (
                                  <TextField
                                        {...field}
                                        autoComplete="new-password"
                                        disabled={formDefaultValues.accountId != null}
                                        error={!!errors.login}
                                        label={translate('common.login')}
                                        required
                                    />
                                )}
                                rules={{ required: true }}
                            />
                        )}
                  </Grid>
                </Grid>
              </FormElement>
            </>
            )}
        </Grid>
      </DialogContent>

      <CustomActionButtons
            isLoading={isLoading}
            onClose={onClose}
        />
    </UpdateUserForm>
  );
});
